// client/src/utils/marketerCPSCache.js

const CACHE_PREFIX = 'MARKETER_CPS_';
const CACHE_TTL = 5 * 60 * 1000; // 5분

// sessionStorage 헬퍼
const ss = typeof window !== 'undefined' ? window.sessionStorage : null;

function cacheSet(key, value) {
  if (!ss) return;
  try {
    ss.setItem(key, JSON.stringify({
      data: value,
      timestamp: Date.now(),
      uid: window.__ME__?.id  // ✅ 사용자 ID 함께 저장
    }));
  } catch (e) {
    console.error('CPS 캐시 저장 실패:', e);
  }
}

function cacheGet(key) {
  if (!ss) return null;
  try {
    const cached = ss.getItem(key);
    if (!cached) return null;
    
    const { data, timestamp, uid } = JSON.parse(cached);
    
    // ✅ TTL 체크
    if (Date.now() - timestamp > CACHE_TTL) {
      ss.removeItem(key);
      return null;
    }
    
    // ✅ 사용자 검증
    const currentUid = window.__ME__?.id;
    
    // currentUid가 없으면 null 반환
    if (!currentUid) {
      return null;
    }
    
    // currentUid가 있는데 캐시 uid와 다르면 삭제
    if (uid && uid !== currentUid) {
      console.warn('CPS 캐시 사용자 불일치, 삭제:', key);
      ss.removeItem(key);
      return null;
    }
    
    return data;
  } catch (e) {
    console.error('CPS 캐시 조회 실패:', e);
    return null;
  }
}

// 프로젝트 목록 캐시
export function cacheCPSProjectList(uid, projects, filter = 'all') {
  cacheSet(`${CACHE_PREFIX}LIST:${uid}:${filter}`, projects);
}

export function readCPSProjectList(uid, filter = 'all') {
  return cacheGet(`${CACHE_PREFIX}LIST:${uid}:${filter}`);
}

// 프로젝트 상세 캐시
export function cacheCPSDetail(projectId, roundNumber, detail) {
  cacheSet(`${CACHE_PREFIX}DETAIL:${projectId}:${roundNumber}`, detail);
}

export function readCPSDetail(projectId, roundNumber) {
  return cacheGet(`${CACHE_PREFIX}DETAIL:${projectId}:${roundNumber}`);
}

// 특정 프로젝트의 특정 회차 캐시 무효화
export function invalidateCPSDetail(projectId, roundNumber) {
  if (!ss || !projectId || !roundNumber) return;
  try {
    const key = `${CACHE_PREFIX}DETAIL:${projectId}:${roundNumber}`;
    ss.removeItem(key);
    console.log(`🗑️ [CPS 캐시 무효화] ${projectId} - ${roundNumber}회차`);
  } catch (e) {
    console.error('CPS 캐시 무효화 실패:', e);
  }
}

// 캐시 초기화
export function clearCPSCache(uid) {
  if (!ss) return;
  try {
    const keys = Object.keys(ss);
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        ss.removeItem(key);
      }
    });
  } catch (e) {
    console.error('CPS 캐시 초기화 실패:', e);
  }
}

// 백그라운드 로드
export async function warmCPSProjects(uid) {
  if (!uid) return;
  
  try {
    // 1. 프로젝트 목록 조회 (filter=all)
    const res = await fetch('/api/marketer/cps-settlements?filter=all', {
      credentials: 'include'
    });
    const data = await res.json();
    
    if (res.ok && data.ok && data.projects) {
      cacheCPSProjectList(uid, data.projects, 'all');
      
      // 2. 첫 번째 프로젝트의 현재 회차 상세 조회
      if (data.projects.length > 0) {
        const firstProject = data.projects[0];
        const projectId = firstProject.id;
        const roundNumber = firstProject.currentRound || 1;
        
        try {
          const detailRes = await fetch(
            `/api/marketer/cps-settlements/${projectId}?roundNumber=${roundNumber}`,
            { credentials: 'include' }
          );
          const detailData = await detailRes.json();
          
          if (detailRes.ok && detailData.ok) {
            cacheCPSDetail(projectId, roundNumber, detailData);
          }
        } catch (e) {
          console.error('CPS 상세 백그라운드 로드 실패:', e);
        }
      }
      
      // 3. filter=in_progress 목록 조회 (백그라운드)
      try {
        const resProgress = await fetch(
          '/api/marketer/cps-settlements?filter=in_progress',
          { credentials: 'include' }
        );
        const dataProgress = await resProgress.json();
        
        if (resProgress.ok && dataProgress.ok) {
          cacheCPSProjectList(uid, dataProgress.projects, 'in_progress');
        }
      } catch (e) {
        console.error('CPS 진행중 목록 백그라운드 로드 실패:', e);
      }
    }
  } catch (e) {
    console.error('CPS 프로젝트 목록 백그라운드 로드 실패:', e);
  }
}

